/**
 * Phase-overlay styling — shared between Ledger strips, legend, and any
 * future surface (Brief, Spy Health). Single source of truth so the
 * lunar / circadian palette stays coherent.
 *
 * Backend lives in /app/backend/routers/overlay.py.
 */

// Lunar — calendar convention (northern hemisphere): NEW = filled dark
// circle (no moon visible), FULL = outline only (bright edge against night
// sky). Crescents and gibbous use the astronomical moon emoji block — the
// only unicode set where direction-of-lighting is unambiguous. Geometric
// shapes (◗ ◕ ◔ ◖) were wrong: half-discs read as quarters and the quadrant
// glyphs read as the opposite phase.
// Iter 31.204 (03 Jun 2026): FULL fill corrected (was amber-300, now outline).
// Iter 31.224 (06 Jun 2026): crescent + gibbous glyphs corrected per Six —
// "moon wrong, correct on half moons".
export const LUNAR_META = {
    "new":               { label: "New",        glyph: "●",  cls: "bg-black text-white border border-white/20",                       dot: "bg-black" },
    "waxing-crescent":   { label: "Wax. cres.", glyph: "🌒", cls: "bg-slate-700 text-white border border-white/15",                   dot: "bg-slate-700" },
    "first-quarter":     { label: "1st qtr",    glyph: "◑",  cls: "bg-slate-500 text-white border border-white/15",                   dot: "bg-slate-500" },
    "waxing-gibbous":    { label: "Wax. gib.",  glyph: "🌔", cls: "bg-slate-300 text-black border border-black/10",                   dot: "bg-slate-300" },
    "full":              { label: "Full",       glyph: "○",  cls: "bg-transparent text-ink border-2 border-ink/70",                   dot: "bg-transparent border-2 border-ink/70" },
    "waning-gibbous":    { label: "Wan. gib.",  glyph: "🌖", cls: "bg-slate-300 text-black border border-black/10",                   dot: "bg-slate-300" },
    "last-quarter":      { label: "Last qtr",   glyph: "◐",  cls: "bg-slate-500 text-white border border-white/15",                   dot: "bg-slate-500" },
    "waning-crescent":   { label: "Wan. cres.", glyph: "🌘", cls: "bg-slate-700 text-white border border-white/15",                   dot: "bg-slate-700" },
};

// Circadian — washes the day from cold predawn through warm midday and back
// down into the cobalt of dead-of-night. Same scale as the backend bands.
export const CIRCADIAN_META = {
    "dead-of-night": { label: "Dead of night", cls: "bg-[#1a2233] text-white border border-white/25",      dot: "bg-[#1a2233]" },
    "predawn":       { label: "Predawn",       cls: "bg-[#3a4a66] text-white border border-white/20",      dot: "bg-[#3a4a66]" },
    "morning":       { label: "Morning",       cls: "bg-[#c9b56b] text-black border border-black/10",      dot: "bg-[#c9b56b]" },
    "late-morning":  { label: "Late morning",  cls: "bg-[#d6c891] text-black border border-black/10",      dot: "bg-[#d6c891]" },
    "midday":        { label: "Midday",        cls: "bg-[#e9c46a] text-black border border-black/10",      dot: "bg-[#e9c46a]" },
    "afternoon":     { label: "Afternoon",     cls: "bg-[#cf8e4f] text-black border border-black/10",      dot: "bg-[#cf8e4f]" },
    "evening":       { label: "Evening",       cls: "bg-[#7b4b3d] text-white border border-white/20",      dot: "bg-[#7b4b3d]" },
    "night":         { label: "Night",         cls: "bg-[#2c2e4a] text-white border border-white/25",      dot: "bg-[#2c2e4a]" },
};

export function lunarMeta(label) {
    return LUNAR_META[label] || { label: label || "—", glyph: "·", cls: "bg-ink/20 text-ink", dot: "bg-ink/20" };
}

export function circadianMeta(label) {
    return CIRCADIAN_META[label] || { label: label || "—", cls: "bg-ink/20 text-ink", dot: "bg-ink/20" };
}

// Browser tz offset in *hours* from UTC (e.g. London winter = 0, summer = 1).
// Used to call /shed/overlay/ledger?tz_offset_hours=…
export function browserTzOffsetHours() {
    // getTimezoneOffset returns minutes WEST of UTC, so flip the sign.
    return -new Date().getTimezoneOffset() / 60;
}
